import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dog,
  ArrowRight,
  ArrowLeft,
  Share2,
  RotateCcw,
  Sparkles,
  Loader2,
  Heart,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BreedQuiz() {
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/breed-quiz/questions`);
        setQuestions(res.data.questions || []);
      } catch (err) {
        toast.error('Could not load quiz');
      }
    })();
  }, []);

  const total = questions.length;
  const current = questions[step];
  const isLast = step === total - 1;

  const selectOption = (optionId) => {
    if (!current) return;
    const nextAnswers = { ...answers, [current.id]: optionId };
    setAnswers(nextAnswers);
    if (isLast) {
      submit(nextAnswers);
    } else {
      setStep(step + 1);
    }
  };

  const goBack = () => setStep(Math.max(0, step - 1));

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(finalAnswers).map(([qid, oid]) => ({
          question_id: qid,
          option_id: oid,
        })),
      };
      const res = await axios.post(`${API}/breed-quiz/submit`, payload);
      setResult(res.data);
    } catch (err) {
      toast.error('Could not compute your breed match');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
  };

  const share = async () => {
    const url = `${window.location.origin}/breed-quiz`;
    const text = result
      ? `My AADC breed match: ${result.breeds.map((b) => b.breed).join(', ')}. Take the quiz:`
      : 'Take the AADC Dog Breed Match quiz:';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'AADC Breed Match', text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success('Link copied to clipboard!');
      }
    } catch {}
  };

  if (questions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10 max-w-3xl" data-testid="breed-quiz-page">
      <SEO
        title="Which Dog Breed Matches You? — 60-Second Breed Quiz"
        description="Tell us about your lifestyle in 8 quick questions and we'll match you with 3 dog breeds (plus a rescue recommendation) that fit your life. Free, science-informed, and instant."
        keywords={[
          'dog breed quiz',
          'which dog breed is right for me',
          'breed selector',
          'dog personality quiz',
          'right dog for my lifestyle',
          'AADC breed match',
        ]}
        url="/breed-quiz"
      />

      {!result && (
        <>
          <header className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3 text-amber-600">
              <Dog className="w-6 h-6" />
              <span className="text-sm font-medium uppercase tracking-wider">
                AADC Breed Match
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">
              Which breed fits your life?
            </h1>
            <p className="text-muted-foreground">
              {total} quick questions · takes about 60 seconds
            </p>
          </header>

          <div className="mb-6">
            <Progress value={((step + 1) / total) * 100} />
            <p className="text-xs text-muted-foreground text-center mt-2">
              Question {step + 1} of {total}
            </p>
          </div>

          <Card>
            <CardContent className="p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-heading mb-6 leading-snug">
                {current.question}
              </h2>
              <div className="space-y-3">
                {current.options.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => selectOption(opt.id)}
                    disabled={submitting}
                    className="w-full text-left p-4 rounded-lg border-2 border-muted hover:border-amber-500 hover:bg-amber-50 transition-colors disabled:opacity-50"
                    data-testid={`quiz-option-${opt.id}`}
                  >
                    <span className="font-medium">{opt.label}</span>
                  </button>
                ))}
              </div>
              {step > 0 && (
                <Button
                  variant="ghost"
                  onClick={goBack}
                  className="mt-4"
                  disabled={submitting}
                  data-testid="quiz-back"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              {submitting && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Finding your match…
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {result && (
        <>
          <header className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-3 text-amber-600">
              <Sparkles className="w-6 h-6" />
              <span className="text-sm font-medium uppercase tracking-wider">
                Your top matches
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">
              Here are 3 breeds that fit you
            </h1>
            {result.source === 'ai' && (
              <p className="text-xs text-muted-foreground">Curated by Claude AI</p>
            )}
          </header>

          <div className="space-y-4 mb-8">
            {result.breeds.map((b, idx) => (
              <Card
                key={b.breed}
                className={idx === 0 ? 'border-l-4 border-l-amber-500' : ''}
                data-testid={`match-${idx}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div
                      className={`rounded-full w-10 h-10 flex items-center justify-center text-white font-bold shrink-0 ${
                        idx === 0 ? 'bg-amber-500' : 'bg-muted-foreground'
                      }`}
                    >
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                        {b.breed}
                        {idx === 0 && <Heart className="w-4 h-4 fill-amber-500 text-amber-500" />}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {b.reason}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 justify-center mb-10">
            <Button
              onClick={() => navigate('/register')}
              size="lg"
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="quiz-cta-join"
            >
              Join AADC free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" onClick={share} data-testid="quiz-share">
              <Share2 className="w-4 h-4 mr-2" />
              Share this quiz
            </Button>
            <Button variant="ghost" onClick={reset} data-testid="quiz-retake">
              <RotateCcw className="w-4 h-4 mr-2" />
              Retake
            </Button>
          </div>

          <div className="text-center text-xs text-muted-foreground border-t pt-5">
            <p>
              No dog is one-size-fits-all — this is a starting point. Meet breeders and shelter
              dogs in person before deciding.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
